import type { TeamIncomeWorkerType, TeamIncomeWorkerUserType } from '@econtractor/types';
import type { WorkerFormRow } from './types';

const roundMoney = (value: unknown): number => Math.round((Number(value) || 0) * 100) / 100;

const fromSaved = (row: TeamIncomeWorkerType, source: WorkerFormRow['source']): WorkerFormRow => {
    const userId = Number(row.workerUserId) || 0;
    return {
        key: String(userId),
        userId,
        userName: String(row.userName || ''),
        userEmail: String(row.userEmail || ''),
        userPicture: row.userPicture || null,
        amount: roundMoney(row.amount),
        amountRate: roundMoney(row.amountRate),
        source,
        persisted: true,
        dt: Number(row.dt) || 0,
        ownerName: String(row.ownerName || ''),
        ownerEmail: String(row.ownerEmail || ''),
        ownerPicture: row.ownerPicture || null,
    };
};

const fromTeamExecutor = (user: TeamIncomeWorkerUserType): WorkerFormRow => {
    const userId = Number(user.ID) || 0;
    return {
        key: String(userId),
        userId,
        userName: String(user.userName || ''),
        userEmail: String(user.userEmail || ''),
        userPicture: user.userPicture || null,
        amount: 0,
        amountRate: 0,
        source: 'team',
        persisted: false,
        dt: 0,
        ownerName: '',
        ownerEmail: '',
        ownerPicture: null,
    };
};

export const mergeTeamWorkers = (
    teamExecutors: TeamIncomeWorkerUserType[] | null | undefined,
    workers: TeamIncomeWorkerType[] | null | undefined,
): WorkerFormRow[] => {
    const saved = Array.isArray(workers) ? workers : [];
    const executors = Array.isArray(teamExecutors) ? teamExecutors : [];
    const savedByUser = new Map<number, TeamIncomeWorkerType>();
    for (const row of saved) {
        const userId = Number(row.workerUserId) || 0;
        if (userId) savedByUser.set(userId, row);
    }

    const teamIds = new Set<number>();
    const teamRows: WorkerFormRow[] = executors.map((user) => {
        const userId = Number(user.ID) || 0;
        teamIds.add(userId);
        const existing = savedByUser.get(userId);
        return existing ? fromSaved(existing, 'team') : fromTeamExecutor(user);
    });

    const extraRows = saved
        .filter((row) => !teamIds.has(Number(row.workerUserId) || 0))
        .map((row) => fromSaved(row, 'manual'));

    return [...teamRows, ...extraRows];
};

export const sumWorkerAmounts = (rows: WorkerFormRow[]): number =>
    rows.reduce((sum, row) => sum + roundMoney(row.amount), 0);

export const sumWorkerAmountRates = (rows: WorkerFormRow[]): number =>
    rows.reduce((sum, row) => sum + roundMoney(row.amountRate), 0);

export const toSavePayload = (rows: WorkerFormRow[]) =>
    rows
        .filter((row) => row.persisted || row.source === 'manual' || roundMoney(row.amount) > 0)
        .map((row) => ({
            userId: row.userId,
            amount: roundMoney(row.amount),
            amountRate: roundMoney(row.amountRate),
        }));
